import { t } from "@lingui/core/macro";

import { type AttemptDatasetManifest } from "_/data/projects";

import { type Tuple } from "_/types";

import {
  type EncoderData,
  type ForceData,
  type PrintDataRecords,
} from "./parseBin";
import {
  encoderMovingAverage,
  isUsableEncoderError,
} from "./encoder-high-pass";

export type LabSensorType = `lab:${string}`;
export type EncoderSensorType = "xError" | "yError" | "xyError";
export type AnalyzeSensorType = "toolForce" | EncoderSensorType | LabSensorType;

export const isLabSensorType = (type: string): type is LabSensorType =>
  /^lab:[A-Za-z0-9_]{1,64}$/.test(type);

export const isEncoderSensorType = (
  type: AnalyzeSensorType
): type is EncoderSensorType =>
  type === "xError" || type === "yError" || type === "xyError";

export function getAvailableSensorTypes(
  manifest: AttemptDatasetManifest[],
  includeAdditionalSensors = false
) {
  const sensorTypes: Array<{
    value: AnalyzeSensorType;
    label: string;
    datasetKinds: string[];
  }> = [
    {
      value: "toolForce",
      label: t`components.controls.analyze.nozzle-force`,
      datasetKinds: ["toolForce0", "toolForce1"],
    },
    {
      value: "xError",
      label: t`components.controls.analyze.x-encoder-error`,
      datasetKinds: ["axesEncoders"],
    },
    {
      value: "yError",
      label: t`components.controls.analyze.y-encoder-error`,
      datasetKinds: ["axesEncoders"],
    },
    {
      value: "xyError",
      label: t`components.controls.analyze.total-xy-error`,
      datasetKinds: ["axesEncoders"],
    },
  ];

  const labSensors = Array.from(
    new Set(
      manifest
        .filter((dataset) => dataset.samples > 0)
        .map((dataset) => dataset.kind)
        .filter(isLabSensorType)
    )
  ).map((kind) => ({ value: kind, label: kind, datasetKinds: [kind] }));

  return [...sensorTypes, ...labSensors].filter(
    (sensor) =>
      (includeAdditionalSensors || sensor.value === "toolForce") &&
      manifest.some(
        (dataset) =>
          dataset.samples > 0 && sensor.datasetKinds.includes(dataset.kind)
      )
  );
}

// Keep the load-cell stream's existing gram-force calibration, without rescaling.
export const sensorUnit = (type: AnalyzeSensorType) => {
  switch (type) {
    case "toolForce":
      return "g";
    case "xError":
    case "yError":
    case "xyError":
      return "mm";
    default:
      if (!isLabSensorType(type)) {
        console.warn("Unexpected sensor type:", type);
      }
      return "";
  }
};

export function encoderScalarRecords(
  records: PrintDataRecords<EncoderData>,
  type: EncoderSensorType,
  highPass = false
): PrintDataRecords<ForceData> {
  const xOffsets =
    highPass && (type === "xError" || type === "xyError")
      ? encoderMovingAverage(records, "xError")
      : undefined;
  const yOffsets =
    highPass && (type === "yError" || type === "xyError")
      ? encoderMovingAverage(records, "yError")
      : undefined;

  return {
    length: records.length,
    at: (index) => {
      const record = records.at(index);
      const validX = isUsableEncoderError(record.xError);
      const validY = isUsableEncoderError(record.yError);
      const xError = record.xError - (xOffsets?.[index] ?? 0);
      const yError = record.yError - (yOffsets?.[index] ?? 0);

      let sensorValue = NaN;
      if (type === "xError" && validX) sensorValue = xError;
      if (type === "yError" && validY) sensorValue = yError;
      if (type === "xyError" && validX && validY) {
        sensorValue = Math.hypot(xError, yError);
      }

      return { timestamp: record.timestamp, sensorValue };
    },
  };
}

/** Use the recorded scalar range instead of the force sensor's fixed domain. */
export function recordHistogramDomain<T extends ForceData>(
  records: PrintDataRecords<T>
): Tuple<number> {
  let minimum = Infinity;
  let maximum = -Infinity;

  for (let i = 0; i < records.length; i++) {
    const value = records.at(i).sensorValue;
    if (!Number.isFinite(value)) continue;

    minimum = Math.min(minimum, value);
    maximum = Math.max(maximum, value);
  }

  if (!Number.isFinite(minimum)) return [-0.001, 0.001];
  if (minimum === maximum) {
    const padding = Math.max(0.0005, Math.abs(minimum) * 0.001);

    return [minimum - padding, maximum + padding];
  }

  return [minimum, maximum];
}
